import { useEffect, useMemo, useState } from 'react';
import { useApp } from '../context';
import { formatDuration, isWatched, tmdbRef, type PlexItem } from '../plex';
import { useMeta } from '../meta';
import { linkProps, navigate } from '../router';
import { PlayIcon } from '../components/Icons';
import { ParentsGuideRow, RatingsRow, TitleLogo } from '../components/MetaBits';

/**
 * "Wat zullen we kijken?": een willekeurige titel uit je bibliotheek, met een paar filters.
 * Plex sorteert willekeurig (sort=random); lengte en score filteren we hier.
 */

type Length = 'any' | '90' | '120' | '150';
const FILTER_KEY = 'tumedia.randomFilters';

interface Filters { lib: string; unwatched: boolean; length: Length; genre: string; uhd: boolean; minRating: number }

function savedFilters(): Partial<Filters> {
  try {
    return JSON.parse(localStorage.getItem(FILTER_KEY) ?? '{}') as Partial<Filters>;
  } catch {
    return {};
  }
}

export function RandomPick() {
  const { plex, libraries } = useApp();
  const video = libraries.filter((l) => l.type === 'movie' || l.type === 'show');
  const [f, setF] = useState<Filters>(() => {
    const s = savedFilters();
    return {
      lib: s.lib && video.some((l) => l.key === s.lib) ? s.lib : video.find((l) => l.type === 'movie')?.key ?? video[0]?.key ?? '',
      unwatched: s.unwatched ?? true,
      length: s.length ?? 'any',
      genre: s.genre ?? '',
      uhd: s.uhd ?? false,
      minRating: s.minRating ?? 0,
    };
  });
  const [genres, setGenres] = useState<{ key: string; title: string }[]>([]);
  const [pool, setPool] = useState<PlexItem[] | null>(null);
  const [pos, setPos] = useState(0);
  const [rolling, setRolling] = useState(false);
  const lib = video.find((l) => l.key === f.lib);
  const isShow = lib?.type === 'show';

  useEffect(() => {
    try {
      localStorage.setItem(FILTER_KEY, JSON.stringify(f));
    } catch {
      // niet erg
    }
  }, [f]);

  useEffect(() => {
    if (!f.lib) return;
    plex.genres(f.lib).then(setGenres).catch(() => setGenres([]));
  }, [plex, f.lib]);

  const roll = async () => {
    if (!f.lib) return;
    setRolling(true);
    try {
      const r = await plex.libraryItems(f.lib, {
        sort: 'random',
        unwatched: f.unwatched || undefined,
        genre: f.genre || undefined,
        resolution: f.uhd ? '4k' : undefined,
        start: 0,
        size: 80,
      });
      const maxMs = f.length === 'any' || isShow ? Infinity : Number(f.length) * 60_000;
      const list = r.items.filter((i) => (i.duration ?? 0) <= maxMs && (!f.minRating || (i.audienceRating ?? i.rating ?? 0) >= f.minRating));
      setPool(list);
      setPos(0);
    } catch {
      setPool([]);
    } finally {
      // Even laten "rollen" voor het gevoel
      window.setTimeout(() => setRolling(false), 350);
    }
  };

  // Bij openen en bij elke filterwijziging meteen een keuze
  useEffect(() => {
    void roll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f]);

  const pick = pool?.[pos];
  const next = () => {
    if (pool && pos + 1 < pool.length) setPos(pos + 1);
    else void roll();
  };

  return (
    <main className="page random-page">
      <header className="page-head">
        <h1>Wat zullen we kijken?</h1>
      </header>

      <div className="filterbar random-filters" data-focus-group>
        {video.length > 1 && (
          <select className="chip-select" value={f.lib} onChange={(e) => setF({ ...f, lib: e.target.value, genre: '' })} aria-label="Bibliotheek">
            {video.map((l) => <option key={l.key} value={l.key}>{l.title}</option>)}
          </select>
        )}
        <button className="chip" aria-pressed={f.unwatched} onClick={() => setF({ ...f, unwatched: !f.unwatched })}>Ongezien</button>
        {!isShow && (
          <select className="chip-select" value={f.length} onChange={(e) => setF({ ...f, length: e.target.value as Length })} aria-label="Maximale lengte">
            <option value="any">Elke lengte</option>
            <option value="90">Tot 1,5 uur</option>
            <option value="120">Tot 2 uur</option>
            <option value="150">Tot 2,5 uur</option>
          </select>
        )}
        <select className="chip-select" value={f.genre} onChange={(e) => setF({ ...f, genre: e.target.value })} aria-label="Genre">
          <option value="">Elk genre</option>
          {genres.map((g) => <option key={g.key} value={g.key}>{g.title}</option>)}
        </select>
        <select className="chip-select" value={f.minRating} onChange={(e) => setF({ ...f, minRating: Number(e.target.value) })} aria-label="Minimale score">
          <option value={0}>Elke score</option>
          <option value={6}>Score 6+</option>
          <option value={7}>Score 7+</option>
          <option value={8}>Score 8+</option>
        </select>
        <button className="chip" aria-pressed={f.uhd} onClick={() => setF({ ...f, uhd: !f.uhd })}>4K</button>
      </div>

      {pool && pool.length === 0 && !rolling && (
        <p className="placeholder-row">Niets gevonden met deze filters. Zet er een paar uit.</p>
      )}
      {pick && <PickCard key={pick.ratingKey} item={pick} rolling={rolling} onNext={next} />}
    </main>
  );
}

function PickCard({ item, rolling, onNext }: { item: PlexItem; rolling: boolean; onNext: () => void }) {
  const { plex } = useApp();
  const meta = useMeta(tmdbRef(item));
  const art = plex.image(item.art, 1920, 1080) ?? meta?.backdrop ?? undefined;
  const poster = plex.image(item.thumb, 400, 600);
  const logos = document.documentElement.dataset.logos !== 'off';
  const line = useMemo(() => [
    item.year,
    item.type === 'show' ? (item.childCount ? `${item.childCount} seizoen${item.childCount === 1 ? '' : 'en'}` : null) : formatDuration(item.duration),
    item.contentRating,
    isWatched(item) ? 'Gezien' : null,
  ].filter(Boolean).join(' · '), [item]);

  return (
    <section className={`random-card ${rolling ? 'is-rolling' : ''}`} style={art ? { backgroundImage: `url("${art}")` } : undefined} aria-live="polite">
      <div className="random-body">
        {poster && <img className="random-poster" src={poster} alt="" />}
        <div className="random-info">
          <TitleLogo logo={logos ? meta?.logo : null} title={item.title} />
          <div className="badges"><span className="muted">{line}</span></div>
          <RatingsRow ratings={meta?.ratings} />
          <ParentsGuideRow guide={meta?.parentsGuide} />
          {(item.summary ?? meta?.overview) && <p className="clamp-3">{item.summary ?? meta?.overview}</p>}
          <div className="hero-actions" data-focus-group>
            {item.type !== 'show' && (
              <button className="btn btn-primary" data-autofocus onClick={() => navigate(`/play/${item.ratingKey}`)}><PlayIcon /> Afspelen</button>
            )}
            <a className={`btn ${item.type === 'show' ? 'btn-primary' : ''}`} {...linkProps(`/item/${item.ratingKey}`)}>Meer info</a>
            <button className="btn" onClick={onNext}>🎲 Iets anders</button>
          </div>
        </div>
      </div>
    </section>
  );
}
